import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface StudentEventCarouselProps {
  images: string[];
  alt: string;
  /**
   * `fixed` (default) keeps the card thumbnail box. `natural` lets the image
   * use its own aspect ratio at full width, so a details-page hero shows the
   * whole picture edge to edge without letterboxing.
   */
  fit?: "fixed" | "natural";
}

/**
 * Cover-image carousel of a feed card — the web port of the RN
 * `ImageSliderBox`: one full-width image at a time with dot indicators, plus
 * chevrons to step through the remaining covers.
 */
const StudentEventCarousel: React.FC<StudentEventCarouselProps> = ({
  images,
  alt,
  fit = "fixed",
}) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);

  if (images.length === 0) return null;

  const total = images.length;
  const showPrev = index > 0;
  const showNext = index < total - 1;

  return (
    <div
      className={
        fit === "natural"
          ? "relative w-full bg-gray-100"
          : "pointer-events-none relative aspect-square w-full bg-gray-100 sm:aspect-[4/5]"
      }
    >
      <img
        src={images[index]}
        alt={alt}
        loading="lazy"
        className={
          fit === "natural"
            ? "block h-auto w-full"
            : "h-full w-full object-contain bg-gray-50"
        }
      />

      {total > 1 && (
        <>
          {showPrev && (
            <button
              type="button"
              onClick={() => setIndex((prev) => prev - 1)}
              aria-label={t("Previous image")}
              className="pointer-events-auto absolute left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white transition hover:bg-black/60"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          {showNext && (
            <button
              type="button"
              onClick={() => setIndex((prev) => prev + 1)}
              aria-label={t("Next image")}
              className="pointer-events-auto absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white transition hover:bg-black/60"
            >
              <ChevronRight size={18} />
            </button>
          )}

          <div className="pointer-events-auto absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
            {images.map((image, dotIndex) => (
              <button
                key={image}
                type="button"
                onClick={() => setIndex(dotIndex)}
                aria-label={`${dotIndex + 1} / ${total}`}
                aria-current={dotIndex === index}
                className={`h-1.5 rounded-full transition ${
                  dotIndex === index
                    ? "w-4 bg-white"
                    : "w-1.5 bg-white/60 hover:bg-white"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default StudentEventCarousel;
